/**
 * English UI catalogue of the Settings page and the keymap editor (W2-13 settings-keymap), namespace 'settings'.
 * zh-CN lives in src/i18n/zh-CN/settings.ts (localisation package W3-09).
 */

import { catalogOf, defineMessages } from '../../i18n/define';

export const m = defineMessages('settings', {
  // ---- page ----
  title: 'Settings',
  searchLabel: 'Search settings',
  searchPlaceholder: 'Search settings, shortcuts and rules',
  searchClear: 'Clear the search',
  searchResults: '{count, plural, one {# match} other {# matches}}',
  searchNone: 'Nothing matches "{query}"',
  searchNoneHint: 'Try fewer or other words. Commands and their keys are searched too.',
  navLabel: 'Settings sections',
  navMatches: '{title}: {count, plural, =0 {no matches} one {# match} other {# matches}}',
  resetAll: 'Reset all settings',
  resetAllTitle: 'Reset every setting?',
  resetAllBody:
    'Every setting goes back to its default, including your keyboard shortcuts, volumes and checker rules. Your projects are not touched.',
  resetAllConfirm: 'Reset everything',
  resetAllDone: 'All settings are back to their defaults.',
  backToChart: 'Back to the chart',
  backToLibrary: 'Back to the library',
  changed: 'Changed',
  changedHint: 'Changed from the default',
  resetRow: 'Reset "{title}" to its default',
  resetAlreadyDefault: 'Already the default',
  percentValue: '{value} %',
  timesValue: '{value}x',
  // Units after number fields. Typed values and expressions use them too ("+=500ms" in a seconds field), so a
  // translation keeps the symbols of the expression language (ms, s, px).
  unitMs: 'ms',
  unitSeconds: 's',
  unitPx: 'px',
  unitTimes: 'x',
  sizeB: '{size} B',
  sizeKB: '{size} KB',
  sizeMB: '{size} MB',
  sizeGB: '{size} GB',
  sizeTB: '{size} TB',
  cancel: 'Cancel',
  dismiss: 'Dismiss',
  opensInNewTab: 'opens in a new tab',

  // ---- sections ----
  sectionGeneral: 'General',
  sectionGeneralDesc: 'Language, experience level, appearance and hints.',
  sectionEditing: 'Editing',
  sectionEditingDesc: 'Grid, timeline, paste, fields and the defaults of new notes.',
  sectionAudio: 'Audio',
  sectionAudioDesc: 'Volumes, latency and playback.',
  sectionPreview: 'Preview & skins',
  sectionPreviewDesc: 'How the preview looks and plays, Phira player settings and note skins.',
  sectionKeyboard: 'Keyboard',
  sectionKeyboardDesc: 'Keyboard presets and every shortcut, with conflicts explained.',
  sectionChecker: 'Checker',
  sectionCheckerDesc: 'Which problems the checker looks for, and its limits.',
  sectionStorage: 'Storage',
  sectionStorageDesc: 'Space used in this browser, versions, the trash and backups.',
  sectionFeatures: 'Panels and tools',
  sectionFeaturesDesc: 'Preferences of individual panels and tools.',
  sectionAbout: 'About & licences',
  sectionAboutDesc: 'About Phiedit and the software and fonts it uses.',

  // ---- groups ----
  groupGrid: 'Grid',
  groupTimeline: 'Timeline',
  groupPaste: 'Paste',
  groupFields: 'Fields',
  groupNoteDefaults: 'New note defaults',
  groupVolumes: 'Volumes',
  groupLatency: 'Latency',
  groupPlayback: 'Playback',
  groupPhira: 'Phira player settings',
  groupThresholds: 'Limits (RPE auto-check parameters)',

  // ---- general ----
  locale: 'Language',
  localeDesc: 'The language of the whole editor. Song names and text lines are never translated.',
  localeKeywords: 'language locale english chinese 中文 语言',
  languageEnglish: 'English',
  languageChinese: '简体中文',
  level: 'Experience level',
  levelDesc: 'How much of the editor is shown. Nothing is ever hidden that your chart uses.',
  levelKeywords: 'guided full beginner advanced simple expert mode',
  levelGuided: 'Guided',
  levelGuidedDesc: 'Plain-word hints, fewer fields and tools. Every command stays in the palette.',
  levelFull: 'Full',
  levelFullDesc: 'Every property, layers, the Raw panel and RPE terms.',
  theme: 'Theme',
  themeKeywords: 'theme colours colors dark light contrast appearance',
  themeDark: 'Dark',
  themeLight: 'Light',
  themeHighContrast: 'High contrast',
  reducedMotion: 'Reduce motion',
  reducedMotionDesc: 'Turns off animations of the interface (the chart preview still plays normally).',
  reducedMotionKeywords: 'animation motion accessibility',
  reducedMotionSystem: 'Like the system',
  reducedMotionOn: 'On',
  reducedMotionOff: 'Off',
  uiScale: 'Interface size',
  uiScaleDesc: 'Scales text, buttons and icons. The preview and the timelines keep their own zoom.',
  uiScaleKeywords: 'zoom scale size font text bigger smaller',
  hintBar: 'Hint bar',
  hintBarDesc: 'The line at the bottom that lists what you can do right now.',
  hintBarSentences: 'Sentences',
  hintBarKeys: 'Keys only',
  hintBarOff: 'Off',
  firstUseTips: 'Shortcut tips',
  firstUseTipsDesc: 'After you use the mouse for something a few times, a tip shows its keyboard shortcut.',
  layout: 'Workspace layout',
  layoutDesc: 'How the Chart workspace arranges its panels. Small screens use Laptop automatically.',
  layoutKeywords: 'layout panels compose animate review laptop screen',
  layoutCompose: 'Compose (notes first)',
  layoutAnimate: 'Animate (motion first)',
  layoutReview: 'Review (large preview)',
  layoutLaptop: 'Laptop (small screens)',
  tour: 'Guided tour',
  tourDesc: 'A two-minute walk through the editor. Reset brings back the offer to take it.',
  tourStatusDone: 'Taken',
  tourStatusNotYet: 'Not taken yet',
  tourStart: 'Take the tour',
  tourAgain: 'Take it again',
  tourUnavailable: 'The tour is not available in this version.',

  // ---- editing ----
  defaultSnap: 'Beat snap of new projects',
  defaultSnapDesc: 'The grid new notes and keyframes snap to: 1/4 means four steps per beat.',
  defaultSnapKeywords: 'snap grid division beat quantize',
  xGrid: 'X grid lines',
  xGridDesc: 'How many vertical guide lines the note lane shows for snapping notes sideways.',
  snapPlayhead: 'Snap the playhead to the grid when paused',
  snapPlayheadDesc: 'When playback pauses, the playhead moves to the nearest grid line (like RPE).',
  snapPlayheadKeywords: 'playhead pause snap grid',
  scrollSensitivity: 'Scroll speed',
  scrollSensitivityDesc: 'How far the timeline moves per wheel or trackpad step.',
  scrollSensitivityKeywords: 'scroll wheel trackpad speed sensitivity',
  playheadPosition: 'Playhead height',
  playheadPositionDesc: 'Where the playhead stays during playback, measured from the bottom of the timeline.',
  jumpOnUndo: 'Jump to the change on undo',
  jumpOnUndoDesc: 'Undo and redo scroll to what they changed and select it.',
  pasteMode: 'Paste',
  pasteModeDesc: 'With a ghost you see the result and click to place it; immediately pastes at once (RPE behaviour).',
  pasteKeywords: 'paste clipboard copy ghost',
  pasteGhost: 'With a ghost',
  pasteImmediate: 'Immediately',
  pasteAnchor: 'Paste at',
  pasteAnchorDesc: 'Where pasted notes and keyframes start.',
  pasteAnchorMouse: 'The mouse',
  pasteAnchorPlayhead: 'The playhead',
  beatSyntax: 'Beat fields: "12.1" means',
  beatSyntaxDesc: 'How typed beats with a dot are read. The field always shows what it understood.',
  beatSyntaxKeywords: 'beat syntax decimal rpe subdivision field',
  beatSyntaxDefault: '12.1 beats',
  beatSyntaxRpe: '12 + 1 snap step (RPE)',
  beatDisplay: 'Beats are shown as',
  beatDisplayDesc: 'How beats are written in fields, lists and the time readout.',
  beatDisplayBar: 'bar 4 . beat 1 + 3/4',
  units: 'Units',
  unitsDesc: 'Friendly units (%, seconds) or the raw numbers RPE writes. Each field can still switch on its own.',
  unitsKeywords: 'units raw rpe percent friendly',
  unitsFriendly: 'Friendly',
  unitsRpe: 'RPE raw',
  autoKey: 'Auto-key on in new sessions',
  autoKeyDesc: 'Dragging a line in the preview adds keyframes at the playhead without pressing K.',
  autoKeyKeywords: 'auto key keyframe record animate',
  noteAbove: 'Side',
  noteAboveDesc: 'Which side of the line new notes fall from.',
  noteAboveYes: 'Above the line',
  noteAboveNo: 'Below the line',
  noteFake: 'Decoration (fake) notes',
  noteFakeDesc: 'New notes are decoration only: never judged, never counted.',
  noteSpeed: 'Speed',
  noteSpeedDesc: 'Speed multiplier of new notes (1 = the line speed).',
  noteYOffset: 'Height offset',
  noteYOffsetDesc: 'Extra distance from the line, in RPE units.',
  noteSize: 'Width',
  noteSizeDesc: 'Width multiplier of new notes.',
  noteAlpha: 'Opacity',
  noteAlphaDesc: 'Opacity of new notes, 0 (invisible) to 255 (opaque).',
  noteVisibleTime: 'Appear time',
  noteVisibleTimeDesc: 'New notes appear only this many seconds before they are hit.',
  noteVisibleTimeOff: 'Always visible',
  noteVisibleTimeOn: 'Limited',

  // ---- audio ----
  volumeMaster: 'Master volume',
  volumeMusic: 'Music',
  volumeHitsounds: 'Hit sounds',
  volumeMetronome: 'Metronome',
  volumeKeywords: 'volume sound loudness audio',
  latency: 'Audio latency correction',
  latencyDesc: 'Added to the delay your device reports, so notes and sound line up. Calibrate measures it for you.',
  latencyKeywords: 'latency delay offset sync bluetooth calibrate audio',
  calibrate: 'Calibrate...',
  calibrateDone: 'Latency correction set to {ms} ms.',
  calibrateUnavailable: 'Calibration is not available in this version.',
  hitsoundDelayTapFlick: 'Hit sound delay: Tap and Flick',
  hitsoundDelayDrag: 'Hit sound delay: Drag',
  hitsoundDelayDesc: 'Plays these hit sounds earlier (negative) or later (positive) than the note.',
  spaceStops: 'When playback stops',
  spaceStopsDesc: 'Where the playhead goes when you stop playing.',
  spaceStopsKeywords: 'space stop return playback',
  spaceStopsStay: 'Stay where it stopped',
  spaceStopsReturn: 'Return to where it started',
  preservePitch: 'Keep pitch when slowed down',
  preservePitchDesc:
    'Slower playback keeps the music in tune. Timing is then a little less exact; the editor shows when.',
  preservePitchKeywords: 'pitch rate slow speed tempo',

  // ---- preview ----
  previewMode: 'Preview opens in',
  previewModeDesc: 'Edit shows guides and handles; Clean shows exactly what players see.',
  previewModeEdit: 'Edit mode',
  previewModeClean: 'Clean mode',
  renderScale: 'Render resolution',
  renderScaleDesc: 'Lower is faster on slow machines; higher is sharper.',
  renderScaleKeywords: 'resolution quality sharp performance dpi',
  renderScaleAuto: 'Automatic',
  fpsCap: 'Frame rate limit',
  fpsCapDesc: 'Limits how often the preview redraws (saves battery).',
  fpsCapKeywords: 'fps frame rate battery performance',
  fpsCapNone: 'No limit',
  noteScale: 'Note size',
  noteScaleDesc: 'Phira player setting: the size of every note.',
  phiraKeywords: 'phira player preview',
  doubleHint: 'Highlight simultaneous notes',
  doubleHintDesc: 'Phira player setting: notes hit at the same time get a highlighted skin.',
  particles: 'Hit particles',
  particlesDesc: 'Phira player setting: particles around hit effects.',
  aspect: 'Aspect ratio',
  aspectDesc: "Phira player setting: preview with another aspect ratio than the chart's own.",
  aspectOff: "Use the chart's",
  aspectOn: 'Override',
  aggressiveCull: 'Aggressive note culling',
  aggressiveCullDesc: 'Phira player setting (on by default): hides notes off screen exactly like Phira does.',
  strictHitColor: 'Autoplay hit colours like Phira',
  strictHitColorDesc: 'Phira ignores tinted hit effects in autoplay; keep this on to see the same.',
  msaa: 'Smooth edges (MSAA)',
  msaaDesc: 'Phira player setting: antialiasing. Costs performance.',
  apFcIndicator: 'AP/FC judge-line indicator',
  apFcIndicatorDesc:
    'Phira player setting (on by default): the judge line follows the Perfect / Good / miss colour ladder in judged play. Off, it stays white.',

  // ---- skins ----
  skinsTitle: 'Skins',
  skinsKeywords: 'skin respack resource pack notes hit effect import zip phigros',
  skinsDesc:
    'The skin draws notes and hit effects in the preview. Import a Phira resource pack (.zip) to see your chart as players with that skin will.',
  skinImport: 'Import skin (.zip)',
  skinImporting: 'Importing...',
  skinDefaultLabel: 'Skin used by the preview',
  skinAutomatic: 'Automatic',
  skinAutomaticDesc: 'The Phigros skin from local files when present, otherwise the skin drawn by Phiedit.',
  skinAutomaticNow: 'Recommended. Uses {name} now.',
  skinProblems: 'Skipped while choosing the skin:',
  skinSourceProcedural: 'Drawn by Phiedit',
  skinSourceLocal: 'Phigros skin from local files',
  skinSourceRespack: 'Imported resource pack',
  skinBy: 'by {author}',
  skinInUse: 'In use',
  skinRemove: 'Remove skin "{name}"',
  skinRemoveTitle: 'Remove "{name}"?',
  skinRemoveBody: 'The resource pack is deleted from this browser. Import it again to use it later.',
  skinRemoveConfirm: 'Remove',
  skinRemoved: 'Removed "{name}".',
  skinInstalled: 'Now using "{name}".',
  skinNotARespack: '"{name}" is not a resource pack. Charts are opened from the library.',
  skinInvalid: 'This resource pack cannot be used.',
  skinImportFailed: 'The skin could not be imported: {detail}',
  skinActivateFailed: 'The skin could not be loaded; the previous one stays in use.',
  skinNone: 'No skins are installed yet.',

  // ---- keyboard ----
  preset: 'Keyboard preset',
  presetDesc:
    'Default is designed for this editor; RPE keeps the keys Re:PhiEdit users know. Everything else is shared.',
  presetKeywords: 'keyboard preset rpe re:phiedit default keys hotkeys',
  presetDefault: 'Default',
  presetDefaultDesc: 'Q W E R place notes, Space plays, Ctrl+K opens the palette.',
  presetRpe: 'RPE',
  presetRpeDesc: 'I O P play and stop, Ctrl+J/K/L set the rate, R ... R places segments.',
  altFree: 'Alt-free keys',
  altFreeDesc:
    'For systems that take Alt for themselves: every Alt shortcut moves to another key or is left to menus and the palette, and Ctrl+Shift replaces Alt while dragging.',
  altFreeKeywords: 'alt free linux window manager option',
  conventionsOfferRpe:
    'Also use the RPE editing conventions? (paste immediately, "12.1" = 12 + 1 snap step, RPE units)',
  conventionsOfferDefault:
    'Also use the default editing conventions? (paste with a ghost, decimal beats, friendly units)',
  conventionsApply: 'Use them',
  conventionsApplied: 'Editing conventions updated.',

  keymapTitle: 'Shortcuts',
  keymapKeywords: 'shortcut shortcuts keymap hotkey hotkeys keys rebind binding conflict 快捷键',
  kmDesc:
    'Click a key to change it, the plus button to add another key, the cross to remove one. Keys you add work in both presets; a key you remove stays removed in the preset it came from. Export takes all your changes along.',
  kmSearchLabel: 'Search shortcuts',
  kmSearchPlaceholder: 'Search commands, keys or situations',
  kmSearchCaptureLabel: 'Find a command by pressing its keys',
  kmSearchCapturePlaceholder: 'Press the keys...',
  kmFindByKeys: 'Find a command by pressing its keys',
  kmFilterLabel: 'Show',
  kmFilterAll: 'All',
  kmFilterChanged: 'Changed ({count})',
  kmFilterConflicts: 'Conflicts ({count})',
  kmFilterShared: 'Shared keys ({count})',
  kmFilterUnbound: 'Without keys',
  kmImport: 'Import...',
  kmExport: 'Export',
  kmImportRpe: 'Import RPE Hotkey.txt...',
  kmResetAll: 'Reset all shortcuts',
  kmNothingToReset: 'You have not changed any shortcut.',
  kmCount: '{count, plural, one {# command} other {# commands}} of {total}',
  kmNone: 'No command matches.',
  kmNoneForQuery: 'No command matches "{query}".',
  kmPageFilter: 'Only the commands that match "{query}" are listed.',
  kmNoneForKeys: 'No command uses these keys.',
  kmColCommand: 'Command',
  kmColContext: 'Where',
  kmColKeys: 'Keys',
  kmColActions: 'Actions',
  kmNoKeys: 'No keys',
  kmUnknownCommand: 'Not in this version',
  legendRed: 'Red: two commands share a key in the same situation; only one works. Change one of them.',
  legendYellow:
    'Shared key (information): a key does different things in different situations; the situation decides. Nothing to fix.',
  legendYours: 'Changed by you',
  legendMac: 'On macOS, Ctrl in a shortcut is Cmd.',
  chipChange: 'Change {chord} of "{command}"',
  chipRemove: 'Remove {chord} from "{command}"',
  chipRestore: 'Restore {chord} for "{command}"',
  chipRemovedSr: '(removed)',
  chipRemovedIn: 'Removed in {preset}',
  chipRemovedInHint: '{chord} is removed only in {preset}; the keys you use now are not affected.',
  chipRestoreIn: 'Restore {chord} for "{command}" in {preset}',
  chipRemovedStale: 'No effect',
  chipRemovedStaleHint: 'This removal of {chord} matches no preset key any more, so it has no effect.',
  chipForget: 'Forget the removal of {chord} for "{command}"',
  announceForgot: 'Forgot the removal of {chord} for "{command}".',
  kmCapturing: 'Press any keys to find the commands that use them. Escape stops; Tab leaves the field.',
  conflictRedShort: 'Conflict',
  conflictYellowShort: 'Shared in another situation',
  conflictRed: '{chord} also runs "{other}" ({context}) in the same situation, so only one of them works.',
  conflictYellow: '{chord} also runs "{other}" in another situation ({context}); the situation decides.',
  conflictFixHint: 'Change or remove one of the two.',
  addShortcut: 'Add a shortcut to "{command}"',
  resetCommand: 'Reset "{command}" to the preset keys',
  commandWithArgs: '{command}: {args}',
  announceSaved: '{chord} now runs "{command}".',
  announceRemoved: 'Removed {chord} from "{command}".',
  announceRestored: 'Restored {chord} for "{command}".',
  announceReset: '"{command}" uses the preset keys again.',
  issuesTitle: '{count, plural, one {One of your shortcuts cannot be used} other {# of your shortcuts cannot be used}}',
  issuesDrop: 'Remove them',
  issuesDropped: 'Removed the shortcuts that could not be used.',
  importDoneTitle: 'Shortcuts imported.',
  importPartialTitle: 'Shortcuts imported, with problems:',
  importFailedTitle: 'The shortcuts could not be imported:',
  importFailedToast: 'The shortcuts could not be imported: {error}',
  importUnreadable: 'The file could not be read.',
  openKeymap: 'Open shortcuts',
  exportDone: 'Your shortcuts were saved as phiedit-keymap.json.',
  rpeImported: '{count, plural, one {# shortcut} other {# shortcuts}} imported from RPE.',
  rpeUnmapped: '{count, plural, one {# RPE function has} other {# RPE functions have}} no counterpart here: {names}',
  rpeImportUnavailable: 'Importing RPE Hotkey.txt files is not available in this version.',
  rpeImportNone: 'No RPE shortcuts were found in this file.',
  rpeImportFailedTitle: 'Nothing was imported from RPE:',
  rpeImportedPartialToast:
    '{count, plural, one {# shortcut} other {# shortcuts}} imported from RPE; {unmapped, plural, one {# RPE function has} other {# RPE functions have}} no counterpart here.',
  resetShortcutsTitle: 'Reset all shortcuts?',
  resetShortcutsBody:
    '{count, plural, one {Your change is} other {Your # changes are}} removed and the preset keys come back.',
  resetShortcutsConfirm: 'Reset shortcuts',
  resetShortcutsDone: 'All shortcuts are back to the preset.',
  recordedToast: 'Shortcut saved: {chord}',

  // ---- context words (where a shortcut works) ----
  ctxEverywhere: 'Everywhere',
  ctxOrSeparator: ' or ',
  ctxNot: 'not {what}',
  ctxLane: 'Note lane',
  ctxMotion: 'Motion columns',
  ctxCurve: 'Curve editor',
  ctxPreview: 'Preview',
  ctxLines: 'Lines panel',
  ctxInspector: 'Inspector',
  ctxTiming: 'Song & Timing',
  ctxDock: 'Dock panels',
  ctxProblems: 'Problems',
  ctxHistory: 'History',
  ctxAssets: 'Assets',
  ctxRaw: 'Raw panel',
  ctxPublish: 'Publish',
  ctxLibrary: 'Library',
  ctxSettings: 'Settings',
  ctxTextInput: 'Typing in a field',
  ctxDialog: 'In a dialog',
  ctxPalette: 'Command palette',
  ctxPlacingHold: 'Placing a hold',
  ctxPlacingSegment: 'Placing a segment',
  ctxPasteGhost: 'Paste ghost',
  ctxGesture: 'While dragging',
  ctxPlaying: 'During playback',
  ctxSelNotes: 'Notes selected',
  ctxSelKeys: 'Keys selected',
  ctxSelSegments: 'Segments selected',
  ctxSelLines: 'Lines selected',
  ctxSelBpm: 'Tempo points selected',
  ctxSelAny: 'Something selected',
  ctxSelSingle: 'One item selected',
  ctxSelMixed: 'Mixed selection',
  ctxColumnFocused: 'A column focused',
  ctxProjectOpen: 'A project open',
  ctxUiMover: 'UI mover line active',
  ctxNotesAllowed: 'Line with notes active',
  ctxWorkspaceTiming: 'Song & Timing workspace',
  ctxWorkspaceChart: 'Chart workspace',
  ctxWorkspacePublish: 'Publish workspace',
  ctxLevelGuided: 'Guided level',
  ctxLevelFull: 'Full level',
  ctxPresetDefault: 'Default keys',
  ctxPresetRpe: 'RPE keys',
  ctxOsMac: 'macOS',
  ctxOsOther: 'Windows and Linux',
  ctxToolSelect: 'Select tool',
  ctxToolTap: 'Tap tool',
  ctxToolDrag: 'Drag tool',
  ctxToolFlick: 'Flick tool',
  ctxToolHold: 'Hold tool',
  ctxToolEraser: 'Eraser',

  // ---- command categories ----
  catFile: 'File',
  catEdit: 'Edit',
  catSelect: 'Select',
  catView: 'View',
  catPlayback: 'Playback',
  catNotes: 'Notes',
  catMotion: 'Motion',
  catLines: 'Lines',
  catTiming: 'Timing',
  catTools: 'Tools',
  catPublish: 'Publish',
  catHelp: 'Help and settings',
  catUnknown: 'Other',

  // ---- recorder ----
  recorderTitleAdd: 'New shortcut for "{command}"',
  recorderTitleChange: 'Change the shortcut of "{command}"',
  recorderDesc: 'Press the key combination you want. Esc cancels.',
  recorderPrompt: 'Press the keys now',
  recorderHolding: '{mods}+ ...',
  modCtrl: 'Ctrl',
  modCmd: 'Cmd',
  modAlt: 'Alt',
  modOption: 'Option',
  modShift: 'Shift',
  recorderListeningLabel: 'Recording: press the key combination',
  recorderCapturedLabel: 'Recorded {chord}. Press to record again.',
  recorderHintListening:
    'Every key counts, Tab and Enter included. Keys are recorded by position, so they stay under the same fingers on any keyboard layout.',
  recorderHintCaptured: 'Click the box or press Enter on it to record other keys.',
  recorderReplaces: 'Replaces',
  recorderSame: 'This is already the shortcut.',
  recorderFree: 'Free: no other command uses these keys here.',
  recorderConflictRed: 'Already runs "{command}" in the same situation ({context}).',
  recorderConflictYellow: 'Also runs "{command}" in another situation ({context}); the situation decides.',
  recorderTakeOver:
    '{count, plural, one {Remove the keys from "{command}"} other {Remove the keys from the # other commands}}',
  recorderByChar: 'Match the typed character instead of the key position',
  recorderByCharUnavailable: 'Record a key that types a character first.',
  recorderHold: 'Press and hold (runs while the key is down)',
  recorderHoldUnavailable: 'Character shortcuts cannot be held.',
  recorderSave: 'Save',
  recorderSaveTakeOver: 'Save and resolve',
  recorderNeedKey: 'Press a key combination first.',
  recorderRefusedSave: 'Your browser or system keeps these keys; pick others.',
  recorderSameSave: 'This is already the shortcut.',

  // ---- refused and warned chords ----
  reasonTabsWindows: 'Your browser uses {chord} to open, close or restore tabs and windows; pick another key.',
  reasonTabSwitch: 'Your browser keeps {chord} for switching tabs; pick another key.',
  reasonHistory: 'Your browser uses {chord} to go back or forward, which would leave the editor; pick another key.',
  reasonCloseWindow: '{chord} closes the browser window; pick another key.',
  reasonFullscreen: '{chord} switches the browser to full screen; pick another key.',
  reasonDevtools: '{chord} opens the browser developer tools; pick another key.',
  reasonPrivateWindow: '{chord} opens a private window; pick another key.',
  reasonAddons: '{chord} opens the browser add-ons or tab search; pick another key.',
  reasonBrowserTool: '{chord} is used by the browser itself; pick another key.',
  reasonScreenshot: '{chord} takes a screenshot in the browser; pick another key.',
  reasonReload: '{chord} reloads the page; pick another key.',
  reasonClearData: '{chord} opens the browser window for clearing browsing data; pick another key.',
  reasonMacApp: 'On macOS, {chord} quits the browser or opens or closes tabs and windows; pick another key.',
  reasonMacWindow: 'On macOS, {chord} hides or minimises the browser; pick another key.',
  reasonMacPrefs: 'On macOS, {chord} opens the browser settings; pick another key.',
  reasonMacCycle: 'On macOS, {chord} switches between browser windows; pick another key.',
  reasonMacHistory:
    'On macOS, {chord} goes back or forward in the browser, which would leave the editor; pick another key.',
  reasonMacAddress: 'On macOS, {chord} jumps to the address bar; pick another key.',
  reasonMacLogout: 'On macOS, {chord} logs you out; pick another key.',
  reasonMacOther: 'On macOS, {chord} is used by the browser or the system; pick another key.',
  reasonCtrlAlt:
    'Ctrl+Alt is AltGr on many keyboard layouts (it types characters such as @ or the euro sign); pick another key.',
  reasonUnreliableMenu:
    'Some browsers open their own menu with {chord}, so it may not always reach Phiedit. Menus and the palette still work.',
  reasonUnreliableAddress:
    '{chord} can move focus to the address bar in some browsers. Menus and the palette still work.',
  reasonUnreliableDownloads: '{chord} opens the downloads list in some browsers. Menus and the palette still work.',
  reasonUnreliableHistory: '{chord} opens the browsing history in some browsers. Menus and the palette still work.',
  reasonUnreliableOther: 'Some browsers use {chord} themselves, so it may not always reach Phiedit.',

  // ---- checker ----
  thresholdXMax: 'Notes too far sideways',
  thresholdXMaxDesc: 'Notes farther than this from the line centre get a tip ("X too large").',
  thresholdKeywords: 'threshold limit auto check rpe parameters',
  thresholdCollision: 'Collision distance',
  thresholdCollisionDesc: 'Notes closer than this horizontally count as overlapping or as a tap right after a flick.',
  thresholdReadTime: 'Shortest reading time',
  thresholdReadTimeDesc: 'Notes visible for less time than this before they are hit get a warning.',
  thresholdDragWindow: 'Drag warning window',
  thresholdDragWindowDesc: 'A tap this soon after a nearby drag gets a warning.',
  thresholdComboWindow: 'Combo window',
  thresholdComboWindowDesc: 'A tap within this time after a nearby flick is an error, after a drag a tip.',
  thresholdNearSimultaneous: 'Almost simultaneous',
  thresholdNearSimultaneousDesc: 'Notes closer in time than this, but not at the same time, get a tip.',
  rulesTitle: 'Rules',
  rulesKeywords: 'rules checker problems warnings errors tips disable enable',
  rulesDesc:
    'Turn off rules you do not need. Problems that make Phira fail to load a chart are never hidden at export.',
  rulesEnableAll: 'Turn every rule on',
  rulesAllOn: 'All of them are on.',
  rulesAllOff: 'All of them are off.',
  rulesCategoryOn: 'All on',
  rulesCategoryOff: 'All off',
  rulesOnCount: '{on} of {total} on.',
  rulesNone: 'The rule list appears when the checker is running.',
  rulesNoneBody: 'Open a chart: the checker starts with it.',
  rulesNoMatch: 'No rule matches "{query}".',
  ruleCatPhira: 'Phira compatibility',
  ruleCatPhiraDesc: 'What Phira cannot load, renders wrongly or ignores.',
  ruleCatGameplay: 'Gameplay',
  ruleCatGameplayDesc: 'What makes a chart unfair or unreadable.',
  ruleCatStyle: 'Style',
  ruleCatStyleDesc: 'Tidiness and RPE habits.',

  // ---- storage ----
  storageTitle: 'Storage',
  storageKeywords: 'storage space quota disk trash backup versions persistence download',
  storageDesc: 'Projects, versions and media are kept in this browser on this device. Back them up from time to time.',
  storageLoading: 'Reading storage information...',
  storageUnavailable: 'Storage information is not available right now.',
  storageUsage: 'Used {used} of {quota}',
  persistOn: 'The browser keeps your data even when the disk is nearly full.',
  persistOff: 'The browser may delete your data when the disk is nearly full.',
  persistRequest: 'Keep my data',
  persistGranted: 'The browser will keep your data.',
  persistDenied:
    'The browser did not agree. Installing Phiedit as an app or bookmarking it often helps; keep backups meanwhile.',
  opfsOn: 'Media are stored in the browser file system (fast).',
  opfsOff: 'Media are stored in the browser database (this browser has no private file system; slower but safe).',
  saveOk: 'Every change is saved.',
  savePending: '{count, plural, one {# change is} other {# changes are}} being saved.',
  saveError: 'Saving failed: {error}',
  saveRestarting: 'The saving process is restarting; your changes are kept until it is back.',
  versionsTitle: 'Versions',
  versionsRetention:
    'Phiedit keeps every version from the last hour, one per hour for two days and one per day for 30 days. Named checkpoints and exports are always kept.',
  versionsBudget:
    'Each project may use up to {budget} for versions (the smaller of 200 MB and a tenth of the space the browser gives Phiedit). Above it, the oldest automatic versions are thinned out.',
  projectsTitle: 'Projects ({count})',
  projectsNone: 'No projects yet.',
  projectsTotal: 'Together they use {size}, largest first.',
  projectsShowAll: 'Show all {count} projects',
  projectsShowLargest: 'Show only the {count} largest',
  projectsColName: 'Project',
  projectsColSize: 'Size',
  projectsColUpdated: 'Changed',
  projectUntitled: 'Untitled',
  trashTitle: 'Trash ({count})',
  trashDesc: 'Deleted projects stay here for {days} days, then they are deleted for good.',
  trashMeta:
    'Deleted {date} · {days, plural, =0 {deleted for good today} one {# day left} other {# days left}} · {size}',
  trashRestore: 'Restore',
  trashRestored: 'Restored "{name}" to the library.',
  trashDelete: 'Delete for good',
  trashDeleteOneTitle: 'Delete "{name}" for good?',
  trashEmpty: 'Empty the trash',
  trashEmptyTitle: 'Delete {count, plural, one {# project} other {# projects}} for good?',
  trashDeleteBody: 'Their versions and media that no other project uses are deleted too. This cannot be undone.',
  trashDeleteConfirm: 'Delete for good',
  trashEmptyConfirm: 'Empty the trash',
  trashDeleted: '{count, plural, one {# project} other {# projects}} deleted for good.',
  storageBusy: 'Please wait...',
  storageActionFailed: 'That did not work; nothing was changed. Try again.',
  backupTitle: 'Backup',
  backupDesc: 'Downloads every project as one .zip: each chart as a .pez package plus its Phiedit project data.',
  backupDownload: 'Download all as backup',
  backupRunning: 'The backup is being prepared.',
  backupNothing: 'There are no projects to back up yet.',
  backupProgress: 'Preparing the backup',
  backupDone: '{count, plural, one {# project} other {# projects}} saved to the backup file.',
  backupFailed: 'The backup could not be made: {detail}',

  // ---- about ----
  aboutTitle: 'About Phiedit',
  aboutKeywords: 'about licence license credits version fonts open source phira',
  aboutName: 'Phiedit',
  aboutTagline: 'A chart editor for Phira that runs in your browser.',
  aboutBuildPublic: 'Public build: only freely licensed assets are included.',
  aboutBuildLocal: 'Local build for development.',
  aboutLocalSkin:
    'The Phigros skin is read from local files on this machine for development; it is never part of a public build.',
  aboutCleanRoom:
    "Phiedit contains no code from Phira (GPL-3.0) or Re:PhiEdit. Compatibility with Phira was built from its public documentation, the chart formats and Phira's observable behaviour.",
  aboutIndependent:
    'Phiedit is an independent project. Phira, Phigros and Re:PhiEdit belong to their respective owners, who do not endorse Phiedit.',
  licLibraries: 'Libraries',
  licFonts: 'Fonts',
  licDocuments: 'Documentation',
  licColName: 'Name',
  licColUse: 'Used for',
  licColLicence: 'Licence',
  licFontsNote:
    'The fonts are licensed under the SIL Open Font License 1.1 and may be used and shared freely with Phiedit.',
  useReact: 'The interface',
  useRadix: 'Accessible dialogs, menus and controls',
  useLucide: 'Icons',
  useVirtual: 'Long lists',
  usePanels: 'Resizable panels',
  useCmdk: 'Command palette',
  useZustand: 'Editor state',
  useFflate: 'Reading and writing .pez and .zip files',
  useGif: 'GIF images in charts',
  useIdb: 'Storage in the browser',
  useYaml: 'Reading and writing info.yml',
  useNotoSc: 'Chinese and Japanese text: interface, song names, text lines',
  useNoto: 'Interface text',
  useSaira: 'Score and combo digits in the preview',
  usePhiraDocs: 'Reference for the RPE chart format, info.yml and extra.json',

  // ---- commands ----
  cmdOpen: 'Open settings',
  cmdKeymapOpen: 'Keyboard shortcuts',
  cmdToggleLevel: 'Full level (show every tool)',
  cmdSetLanguage: 'Change language...',
  cmdSetTheme: 'Change theme...',
  cmdSetHintBar: 'Hint bar style...',
  cmdResetAll: 'Reset all settings...',
  cmdApplyConventions: 'Use the editing conventions of a keyboard preset',
  cmdSetPreset: 'Keyboard preset...',
  cmdToggleAltFree: 'Alt-free keys',
  cmdRecord: 'Change a shortcut...',
  cmdImport: 'Import shortcuts (JSON)...',
  cmdExport: 'Export shortcuts (JSON)',
  cmdImportRpe: 'Import RPE Hotkey.txt...',
  cmdResetShortcuts: 'Reset all shortcuts...',

  // ---- palette ----
  paletteSection: 'Settings: {section}',
  paletteIn: 'Settings > {section}',

  // ---- help ----
  helpSettingsTitle: 'Settings',
  helpSettingsBody:
    'Open Settings with Ctrl+, (Cmd+; on a Mac), from the project menu, or type ":" in the command palette to jump straight to a setting. Every change applies at once; Reset next to a setting brings back its default. Settings belong to you, not to a chart: they are the same in every project.',
  helpShortcutsTitle: 'Change keyboard shortcuts',
  helpShortcutsBody:
    'Settings > Keyboard lists every command. Pick the Default or RPE preset, then click a key to change it, the plus button to add another, or the cross to remove one. Keys your browser keeps for itself are refused with the reason. Red means two commands share a key in the same situation (fix it); yellow means the situation decides. Export your shortcuts as JSON to take them to another computer.',
});

export default catalogOf(m);
